import linkHeaderParser from 'linkheader-parser/dist/linkheader-parser-node';

import type {
  HttpVerb,
  Interceptor,
  InterceptorRequestData,
  APILanguage,
  ContentRangeHeader,
  ParsedContentRange,
  ResponseType,
  PaginationData,
} from './types';

function serialize(dataCollection: Headers | FormData): string {
  let serializedData = '';

  Array.from(dataCollection.entries()).forEach(([key, value]) => {
    serializedData += `${key}=${value}`;
  });

  return serializedData;
}

function isPartialResponse(statusCode: number): boolean {
  return statusCode === 206;
}

function parseContentRange(contentRange: ContentRangeHeader): ParsedContentRange {
  let start = 0;
  let end = 0;
  let totalElements = 0;

  if (!contentRange) {
    return {
      start,
      end,
      totalElements,
    };
  }

  const [paginationRange, total] = contentRange.split(' ')[1].split('/') as [
    `${number}-${number}`,
    `${number}`
  ];
  const paginationData = paginationRange.split('-') as [`${number}`, `${number}`];

  start = Number(paginationData[0]);
  end = Number(paginationData[1]);
  totalElements = Number(total);

  return {
    start,
    end,
    totalElements,
  };
}

function getPaginationDataFromContentRange(contentRange: ContentRangeHeader): PaginationData {
  const headerRange = parseContentRange(contentRange);

  return {
    hasPreviousPage: headerRange.start > 0,
    hasNextPage: headerRange.end + 1 < headerRange.totalElements,
    currentPage: Math.floor(headerRange.start / (headerRange.end - headerRange.start + 1)) + 1,
    totalItems: headerRange.totalElements,
  };
}

const queuedRequests: Map<string, Promise<Response>> = new Map();

export default class RequestBuilder<
  RouteOptions extends {
    routeParams: any;
    queryParams?: any;
    headers?: any;
    response: any;
    responseHeaders: any;
    body: any;
  }
> {
  url: string;

  method: HttpVerb;

  routeParams: RouteOptions['routeParams'];

  queryParams: URLSearchParams;

  headers: Headers;

  response: Response | null;

  body: RouteOptions['body'] | null;

  requestInterceptors: Interceptor<InterceptorRequestData>[];

  protected abortController: AbortController | null = null;

  constructor(url: string, method: HttpVerb, language: APILanguage) {
    this.url = url;
    this.method = method;
    this.routeParams = {};
    this.queryParams = new URLSearchParams();
    this.headers = new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Accept-Language': language,
      'X-Requested-With': 'XMLHttpRequest',
    });
    this.body = null;
    this.response = null;
    this.requestInterceptors = [];
    this.abortController = new AbortController();
  }

  setRouteParam<K extends keyof RouteOptions['routeParams']>(
    key: K,
    value: RouteOptions['routeParams'][K]
  ): this {
    this.routeParams[key] = value;
    return this;
  }

  setRouteParams(params: RouteOptions['routeParams']): this {
    this.routeParams = { ...this.routeParams, ...params };
    return this;
  }

  setQueryParam<K extends keyof NonNullable<RouteOptions['queryParams']>>(
    key: K,
    value: NonNullable<RouteOptions['queryParams']>[K]
  ): this {
    this.queryParams.set(String(key), String(value));
    return this;
  }

  setQueryParams(params: RouteOptions['queryParams']): this {
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        this.setQueryParam(key, value);
      });
    }
    return this;
  }

  setHeader<K extends keyof NonNullable<RouteOptions['headers']>>(
    key: K,
    value: NonNullable<RouteOptions['headers']>[K]
  ): this {
    if (value === undefined) {
      this.headers.delete(String(key));
    } else {
      this.headers.set(String(key), String(value));
    }

    return this;
  }

  setHeaders(headers: NonNullable<RouteOptions['headers']>): this {
    Object.entries(headers).forEach(([key, value]) => {
      this.setHeader(key, value);
    });

    return this;
  }

  setBody(body: RouteOptions['body']): this {
    if ((body as unknown) instanceof FormData) {
      this.headers.delete('Content-Type');
    } else if ((this.body as unknown) instanceof FormData && !this.headers.has('Content-Type')) {
      this.headers.set('Content-Type', 'application/json');
    }
    this.body = body;
    return this;
  }

  paginate(rangeStart: number, rangeEnd: number): this {
    const itemsPerPage = rangeEnd - rangeStart + 1;
    const pageToFetch = Math.floor(rangeStart / itemsPerPage) + 1;

    return this.setHeaders({
      Range: `items=${rangeStart}-${rangeEnd}`,
      Pagination: `page=${pageToFetch},items=${itemsPerPage}`,
    });
  }

  addRequestInterceptor(interceptor: Interceptor<InterceptorRequestData>): this {
    this.requestInterceptors.push(interceptor);
    return this;
  }

  protected applyRequestInterceptors(
    requestData: InterceptorRequestData
  ): Promise<InterceptorRequestData> {
    return this.requestInterceptors.reduce(
      (promiseRequestData, interceptor) => promiseRequestData.then(interceptor),
      Promise.resolve(requestData)
    );
  }

  compileUrl(): string {
    let url = this.url.replace(
      /\/:([a-zA-Z0-9_]+)/gi,
      ($0, $1) => `/${String(this.routeParams[$1])}`
    );

    const queryParams = this.queryParams.toString();

    if (queryParams) {
      url += `?${queryParams.replace(/\+/g, '%20')}`;
    }

    return url;
  }

  getSerializedBody(): string | FormData {
    if (this.body === null) {
      return '';
    }

    return (this.body as any) instanceof FormData
      ? (this.body as FormData)
      : JSON.stringify(this.body);
  }

  async execute({
    cacheOptions = {},
  }: { cacheOptions?: Pick<RequestInit, 'cache' | 'next'> } = {}): Promise<
    ResponseType<RouteOptions['response'], RouteOptions['responseHeaders']>
  > {
    const initialUrl = this.compileUrl();
    const initialBody = this.method === 'GET' ? undefined : this.getSerializedBody();

    const { url, options } = await this.applyRequestInterceptors({
      url: initialUrl,
      options: {
        method: this.method,
        headers: this.headers,
        body: initialBody,
      },
    });

    // istanbul ignore next
    const headerKey = options.headers ? serialize(options.headers as Headers) : '';

    let bodyKey = '';

    // istanbul ignore next
    if (options.body instanceof FormData) {
      bodyKey = serialize(options.body);
    } else if (typeof options.body === 'string') {
      bodyKey = options.body;
    }

    const requestKey = `${options.method} ${url} ${headerKey} ${bodyKey}`;

    let request: Promise<Response>;

    if (queuedRequests.has(requestKey)) {
      request = queuedRequests.get(requestKey)!;
    } else {
      request = fetch(url, {
        ...options,
        ...cacheOptions,
        credentials: 'include',
        signal: this.abortController?.signal,
      });

      queuedRequests.set(requestKey, request);
    }

    this.response = await request;

    const stringifiedBody = await this.response.clone().text();
    let body = stringifiedBody;

    try {
      body = JSON.parse(stringifiedBody);
    } catch {
      // istanbul ignore next
    }

    const headers = this.getResponseHeaders();
    const nativeResponse = this.response!;
    let paginationData: PaginationData = {
      hasPreviousPage: false,
      hasNextPage: false,
      currentPage: 1,
      totalItems: 1,
    };

    if (isPartialResponse(this.response.status)) {
      const contentRange = this.response.headers.get('Content-Range') as ContentRangeHeader;
      paginationData = getPaginationDataFromContentRange(contentRange);
    } else if (Array.isArray(body)) {
      paginationData = {
        hasPreviousPage: false,
        hasNextPage: false,
        currentPage: 1,
        totalItems: body.length,
      };
    }

    if (queuedRequests.has(requestKey)) {
      queuedRequests.delete(requestKey);
    }

    const fullResponse = {
      body,
      headers,
      nativeResponse,
      paginationData,
      statusCode: this.response.status,
    };

    if (!this.response.ok) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw fullResponse;
    }

    return fullResponse;
  }

  getResponseHeaders(): RouteOptions['responseHeaders'] | null {
    if (this.response === null) {
      return null;
    }

    const responseHeaders: RouteOptions['responseHeaders'] = {};

    Array.from(this.response.headers.entries()).forEach(([key, value]) => {
      responseHeaders[key] = key === 'link' ? linkHeaderParser.parse(value.trim()) : value;
    });

    return responseHeaders;
  }

  abort(): boolean {
    if (!this.abortController) {
      return false;
    }

    this.abortController.abort();
    this.abortController = null;

    return true;
  }
}
